import type { ExpAdmissionCheckpoint } from './exp-admission'
import type { ExpAdmission, ExpEstimatedEvidence, ExpExactEvidence, ExpPercentCandidate, ExpValue } from '@/domain/types'
import { GCW_EXP_TABLE_VERSION, gcwExpRequirement } from '@/presets/gcw/exp-table'
import { ExpRequirementEstimator, expRequirementRange, type ExpRequirementRange } from './exp-requirement-estimator'

export function percentageExpRange(ratio: number, precision: number, required: number): ExpRequirementRange {
  const half = .5 * 10 ** -precision / 100
  return { minimum: Math.max(0, Math.ceil(required * Math.max(0, ratio - half) - 1e-7)),
    maximum: Math.min(required - 1, Math.ceil(required * Math.min(1, ratio + half) - 1e-7) - 1) }
}

export function isGcwEstimate(value: ExpValue): boolean {
  return value.estimated === true && value.percentageOnly === true && value.ratio !== null
    && Number.isFinite(value.ratio) && value.ratio >= 0 && value.ratio <= 1
    && (value.estimateSource === 'runtime' || value.estimateSource === 'gcw-table')
    && Number.isSafeInteger(value.absolute) && value.absolute! >= 0
    && Number.isSafeInteger(value.requiredExp) && value.requiredExp! > value.absolute!
}

interface Result { evidence?: ExpEstimatedEvidence; admission: ExpAdmission }
interface Basis { required: number; source: 'runtime' | 'gcw-table'; range: ExpRequirementRange }

/** Session-local percentages are display evidence, never exact accounting evidence. */
export class GcwExpEstimator {
  private readonly runtimeRequirements = new ExpRequirementEstimator()
  private measured = new Map<number, Basis>()
  private locked = new Map<number, Basis>()
  private anchor: ExpExactEvidence | null = null
  private pending: ExpPercentCandidate[] = []
  private latestAt = -Infinity
  private cached: Result | undefined
  private gap = true

  reset(): void {
    this.runtimeRequirements.reset(); this.measured.clear(); this.locked.clear(); this.anchor = null; this.breakContinuity()
  }

  checkpoint(): NonNullable<ExpAdmissionCheckpoint['gcwRequirements']> {
    return [...this.measured].map(([level, basis]) => ({ level, required: basis.required, range: { ...basis.range } }))
  }

  restore(requirements: ExpAdmissionCheckpoint['gcwRequirements']): void {
    for (const basis of requirements ?? []) {
      this.measured.set(basis.level, { required: basis.required, range: basis.range, source: 'runtime' })
      this.runtimeRequirements.seed(basis.level, { absolute: null, ratio: null, requiredExp: basis.required })
    }
  }

  breakContinuity(): void {
    this.pending = []; this.latestAt = -Infinity; this.cached = undefined; this.gap = true
    this.locked.clear()
  }

  observeExact(sample: ExpExactEvidence): void {
    if (sample.level == null || sample.value.estimated) return
    const range = sample.value.requiredExpRange ?? expRequirementRange(sample.value, sample.percentagePrecision)
    const confirmed = this.runtimeRequirements.observe(sample.level, { ...sample.value, requiredExp: undefined }, sample.sourceAt,
      { percentagePrecision: sample.percentagePrecision })
    const required = confirmed.accepted ? confirmed.requiredExp : null
    if (range && required && Number.isSafeInteger(required) && range.maximum < Number.MAX_SAFE_INTEGER) {
      this.measured.set(sample.level, { required, range, source: 'runtime' })
      this.locked.delete(sample.level)
    }
    if (!this.anchor || sample.sourceAt >= this.anchor.sourceAt) {
      this.anchor = sample; this.pending = []; this.gap = false
    }
  }

  seed(sample: ExpExactEvidence): void {
    if (!sample.value.estimated) return this.observeExact(sample)
    if (sample.level == null || !isGcwEstimate(sample.value)) return
    this.anchor = sample; this.gap = false
    const required = sample.value.requiredExp!
    const basis: Basis = { required, source: sample.value.estimateSource!,
      range: sample.value.requiredExpRange ?? { minimum: required, maximum: required } }
    this.locked.set(sample.level, basis)
    // Carry an already confirmed basis; percentage values never train the inference model.
    if (basis.source === 'runtime') this.measured.set(sample.level, basis)
  }

  observe(sample: ExpPercentCandidate, minimumConfidence: number, freshnessMs = 15_000): Result {
    const rejected = (): Result => ({ admission: { status: 'rejected', reason: 'invalid', samples: 0 } })
    if (!Number.isFinite(sample.sourceAt) || !Number.isFinite(sample.deliveredAt) || sample.sourceAt > sample.deliveredAt
      || sample.deliveredAt - sample.sourceAt > freshnessMs || !Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence
      || !Number.isFinite(sample.ratio) || sample.ratio < 0 || sample.ratio > 1 || sample.percentagePrecision !== 2
      || sample.level !== null && (!Number.isInteger(sample.level) || sample.level < 1 || sample.level > 200)
      || sample.level !== null && (sample.levelSourceAt == null || sample.levelSourceAt > sample.deliveredAt || sample.deliveredAt - sample.levelSourceAt > freshnessMs)) return rejected()
    if (sample.sourceAt < this.latestAt) return rejected()
    if (sample.sourceAt === this.latestAt) return this.cached ?? rejected()
    this.latestAt = sample.sourceAt
    const before = this.anchor
    const disconnected = this.gap || !before || sample.sourceAt - before.sourceAt > freshnessMs
      || sample.sourceId !== before.value.sourceId || !!before && sample.level !== null && before.level !== null && Math.abs(sample.level - before.level) > 1
    const changedLevel = !!before && sample.level !== before.level
    const decrease = !!before && !changedLevel && sample.ratio < before.value.ratio! - .00005
    const largeGain = !!before && !changedLevel && sample.ratio - before.value.ratio! > Math.max(.02, (sample.sourceAt - before.sourceAt) / 1_000_000)
    const needsReview = disconnected || before?.value.estimated !== true || changedLevel || decrease || largeGain
    const last = this.pending.at(-1)
    const compatible = last && last.level === sample.level && last.sourceId === sample.sourceId
      && sample.sourceAt - last.sourceAt <= freshnessMs && sample.ratio >= last.ratio - .00005
      && sample.ratio - last.ratio <= .02
    this.pending = needsReview ? [...(compatible ? this.pending.slice(-2) : []), sample] : []
    const count = needsReview ? this.pending.length : 2
    const target = changedLevel || decrease || largeGain ? 3 : 2
    const freshTransition = !(changedLevel || decrease) || (sample.levelSourceAt ?? -Infinity) >= this.pending[0]!.sourceAt
    if (count < target || !freshTransition) {
      return this.cached = { admission: { status: 'pending', reason: changedLevel ? 'level-correction' : decrease ? 'decrease' : largeGain ? 'large-gain' : 'recovery', samples: count } }
    }
    if (disconnected) this.locked.clear()
    let basis = sample.level == null ? undefined : this.locked.get(sample.level) ?? this.measured.get(sample.level)
    const table = sample.level == null ? null : gcwExpRequirement(sample.level)
    if (!basis && table) basis = { required: table, range: { minimum: table, maximum: table }, source: 'gcw-table' }
    if (basis && sample.level != null) this.locked.set(sample.level, basis)
    const absoluteRange = basis ? percentageExpRange(sample.ratio, 2, basis.required) : undefined
    const value: ExpEstimatedEvidence['value'] = { absolute: basis ? Math.min(basis.required - 1, Math.round(basis.required * sample.ratio)) : null,
      ratio: sample.ratio, estimated: true, percentageOnly: true, percentagePrecision: 2, sourceId: sample.sourceId,
      requiredExp: basis?.required ?? null, requiredExpApproximate: basis?.source === 'runtime',
      ...(basis ? { estimateSource: basis.source, requiredExpRange: basis.range, absoluteRange,
        ...(basis.source === 'gcw-table' ? { tableVersion: GCW_EXP_TABLE_VERSION } : {}) } : {}) }
    const evidence: ExpEstimatedEvidence = { value, level: sample.level, levelSourceAt: sample.levelSourceAt,
      sourceAt: sample.sourceAt, deliveredAt: sample.deliveredAt, confidence: sample.confidence,
      percentagePrecision: 2, confirmation: 'consensus' }
    const admission: ExpAdmission = { status: 'accepted', displayStatus: 'accepted', samples: count, value,
      transition: disconnected ? 'rebaseline' : changedLevel ? 'level-up' : 'continuous',
      ...(decrease && !disconnected ? { confirmedLoss: true, change: { id: `gcw:${this.pending[0]!.sourceAt}`, revision: 1,
        fromSourceAt: this.pending[0]!.sourceAt, status: 'confirmed', kind: 'loss', loss: { before: before!, firstAfter: {
          ...evidence, sourceAt: this.pending[0]!.sourceAt,
          value: { ...value, absolute: basis ? Math.min(basis.required - 1, Math.round(basis.required * this.pending[0]!.ratio)) : null,
            ratio: this.pending[0]!.ratio } } } } } : {}) }
    this.anchor = evidence; this.gap = false; this.pending = []
    return this.cached = { evidence, admission }
  }
}
